/**
 * LocationTreeSelect — selector de ubicación con forma de organigrama.
 *
 * Un `<select>` nativo no puede mostrar el árbol ni la ruta completa a la vez:
 * con muchas sedes, plantas y salas la lista es un caos. Este componente:
 *   · pinta el desplegable como el organigrama (sangría por nivel + icono),
 *   · y en el campo muestra SIEMPRE la ruta completa: «Sede › Planta 2 › Sala 3».
 *
 * Es controlado: recibe `value` y avisa con `onChange(valor)`.
 * `extraOptions` permite anteponer opciones sueltas («Sin cambios», «Sin ubicación»).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { buildTreeFlat, buildLocationMap, locationPath, typeIcon, typeLabel } from '../../utils/locationTree';

export default function LocationTreeSelect({
  locations = [],
  value = '',
  onChange,
  placeholder = 'Selecciona una ubicación…',
  extraOptions = [],
  disabled = false,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);

  // El desplegable se pinta en `position: fixed` medido desde el botón: dentro de
  // `.sf2-scroll` o de un modal, un `absolute` quedaría recortado por el overflow.
  const medir = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const alto = Math.min(320, window.innerHeight - 24);
    const abajo = window.innerHeight - r.bottom - 12;
    const arriba = r.top - 12;
    const haciaArriba = abajo < Math.min(180, alto) && arriba > abajo;
    setPos({
      left: Math.min(r.left, Math.max(8, window.innerWidth - r.width - 8)),
      width: Math.max(220, r.width),
      top: haciaArriba ? undefined : Math.round(r.bottom + 4),
      bottom: haciaArriba ? Math.round(window.innerHeight - r.top + 4) : undefined,
      maxHeight: Math.round(Math.max(140, Math.min(alto, haciaArriba ? arriba : abajo))),
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    const onScroll = () => medir();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const arbol = useMemo(() => buildTreeFlat(locations), [locations]);
  const mapa = useMemo(() => buildLocationMap(locations), [locations]);

  const extra = extraOptions.find((o) => String(o.value) === String(value));
  const seleccion = value !== '' && value != null ? mapa.get(String(value)) : null;
  const etiqueta = seleccion ? locationPath(mapa, seleccion.id) : (extra?.label || placeholder);

  const elegir = (nuevo) => {
    onChange?.(nuevo);
    setOpen(false);
  };

  return (
    <div className={`sf2-locsel ${className}`.trim()} ref={wrapRef}>
      <button
        type="button"
        className="sf2-locsel-trigger"
        onClick={() => {
          if (disabled) return;
          setOpen((v) => {
            if (!v) medir();
            return !v;
          });
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={etiqueta}
      >
        <span className={`sf2-locsel-value${seleccion || extra ? '' : ' sf2-locsel-value--empty'}`}>
          {seleccion && <span aria-hidden="true">{typeIcon(seleccion.location_type)} </span>}
          {etiqueta}
        </span>
        <ChevronDownIcon className="sf2-locsel-caret" />
      </button>

      {open && pos && (
        <div
          className="sf2-locsel-pop sf2-locsel-pop--fixed"
          role="listbox"
          style={{
            left: pos.left,
            width: pos.width,
            top: pos.top,
            bottom: pos.bottom,
            maxHeight: pos.maxHeight,
          }}
        >
          {extraOptions.map((o) => (
            <button
              key={`extra-${o.value}`}
              type="button"
              role="option"
              aria-selected={String(o.value) === String(value)}
              className={`sf2-locsel-row${String(o.value) === String(value) ? ' sf2-locsel-row--sel' : ''}`}
              onClick={() => elegir(o.value)}
            >
              <span className="sf2-locsel-name">{o.label}</span>
            </button>
          ))}

          {arbol.length === 0 ? (
            <div className="sf2-locsel-empty">No hay ubicaciones.</div>
          ) : (
            arbol.map((loc) => {
              const sel = String(loc.id) === String(value);
              return (
                <button
                  key={loc.id}
                  type="button"
                  role="option"
                  aria-selected={sel}
                  className={`sf2-locsel-row${sel ? ' sf2-locsel-row--sel' : ''}`}
                  style={{ paddingLeft: 10 + (loc._depth || 0) * 16 }}
                  onClick={() => elegir(String(loc.id))}
                >
                  <span className="sf2-locsel-icon" aria-hidden="true">{typeIcon(loc.location_type)}</span>
                  <span className="sf2-locsel-name">{loc.name}</span>
                  <span className="sf2-locsel-type">
                    {typeLabel(loc.location_type)}
                    {loc.code ? ` · ${loc.code}` : ''}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
